import html2canvas from "html2canvas";

export async function exportDiagramPNG(
  element: HTMLElement,
  filename: string
): Promise<void> {
  // 1. Deep-clone the element
  const clone = element.cloneNode(true) as HTMLElement;

  // 2. Fix Timeline items: IntersectionObserver won't fire on off-screen clones,
  //    so items stay opacity-0 / translate-y-6. Force them visible.
  clone.querySelectorAll("[class]").forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.classList.remove("opacity-0", "translate-y-6");
    if (!htmlEl.classList.contains("opacity-100")) {
      htmlEl.classList.add("opacity-100");
    }
    if (!htmlEl.classList.contains("translate-y-0")) {
      htmlEl.classList.add("translate-y-0");
    }
  });

  // 3. Fix ZoomPanCanvas / TransformWrapper: reset pan/zoom transforms
  //    and remove overflow clipping so full content is captured.
  clone.querySelectorAll("[style]").forEach((el) => {
    const htmlEl = el as HTMLElement;
    const style = htmlEl.style;

    // Reset any translate/scale transforms (zoom-pan wrappers)
    if (style.transform && (style.transform.includes("translate") || style.transform.includes("scale"))) {
      style.transform = "none";
    }

    // Remove overflow hidden/auto that clips content
    if (style.overflow === "hidden" || style.overflow === "auto") {
      style.overflow = "visible";
    }

    // Remove maxHeight constraints
    if (style.maxHeight) {
      style.maxHeight = "none";
    }
  });

  // Also fix CSS class-based overflow
  clone.querySelectorAll(".overflow-hidden, .overflow-auto, .overflow-y-auto, .overflow-x-auto").forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.classList.remove("overflow-hidden", "overflow-auto", "overflow-y-auto", "overflow-x-auto");
    htmlEl.classList.add("overflow-visible");
  });

  // Fix flex-1 / flex containers that collapse when detached from parent context
  clone.querySelectorAll(".flex-1").forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.classList.remove("flex-1");
    htmlEl.style.flex = "none";
  });
  clone.querySelectorAll(".min-h-0").forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.classList.remove("min-h-0");
  });

  // Remove interactive hint overlays (e.g. "Ctrl + Scroll" hint)
  clone.querySelectorAll(".pointer-events-none.absolute").forEach((el) => {
    if (el.textContent?.includes("Ctrl") || el.textContent?.includes("Scroll") || el.textContent?.includes("Pinch")) {
      el.remove();
    }
  });

  // Remove diagram action buttons from export
  clone.querySelectorAll(".diagram-buttons").forEach((el) => el.remove());

  // 4. Find all SVGs and convert ONLY large diagram SVGs to raster <img> tags.
  //    Skip small icon SVGs (lucide-react icons, etc.) to preserve button layout.
  const cloneSvgs = Array.from(clone.querySelectorAll("svg"));
  const origSvgs = Array.from(element.querySelectorAll("svg"));

  // Pre-collect pairs before mutating
  const svgPairs: Array<{ cloneSvg: SVGSVGElement; origSvg: SVGSVGElement | null }> = [];
  for (let i = 0; i < cloneSvgs.length; i++) {
    svgPairs.push({
      cloneSvg: cloneSvgs[i] as SVGSVGElement,
      origSvg: i < origSvgs.length ? (origSvgs[i] as SVGSVGElement) : null,
    });
  }

  for (const { cloneSvg, origSvg } of svgPairs) {
    // Skip small SVGs (icons like lucide-react chevrons, etc.)
    // These are typically 16-24px and html2canvas handles them fine
    const origRect = origSvg?.getBoundingClientRect();
    if (origRect && origRect.width < 40 && origRect.height < 40) {
      continue;
    }

    const serializer = new XMLSerializer();
    let svgString = serializer.serializeToString(cloneSvg);

    if (!svgString.includes("xmlns=")) {
      svgString = svgString.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
    }

    const dataUrl =
      "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);

    const img = document.createElement("img");
    img.src = dataUrl;

    if (origRect && origRect.width > 0) {
      img.style.width = origRect.width + "px";
      img.style.height = origRect.height + "px";
    } else {
      img.style.width = cloneSvg.getAttribute("width") || "100%";
      img.style.height = cloneSvg.getAttribute("height") || "100%";
    }

    // Preserve positioning styles from the original SVG element
    const cloneSvgStyle = window.getComputedStyle(cloneSvg);
    const position = cloneSvg.style.position || cloneSvg.getAttribute("class")?.includes("absolute") ? "absolute" : "";
    if (position === "absolute" || cloneSvg.classList.contains("absolute")) {
      img.style.position = "absolute";
      img.style.inset = "0";
      img.style.pointerEvents = "none";
    }

    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });

    cloneSvg.parentNode?.replaceChild(img, cloneSvg);
  }

  // 5. Position clone off-screen for html2canvas
  // Use the original element's computed dimensions to avoid flex collapse
  const origRect = element.getBoundingClientRect();
  clone.style.position = "fixed";
  clone.style.left = "-9999px";
  clone.style.top = "0";
  clone.style.zIndex = "-1";
  clone.style.width = Math.max(element.scrollWidth, origRect.width) + "px";
  clone.style.height = "auto"; // Let content determine height
  document.body.appendChild(clone);

  // 6. Render with html2canvas
  const canvas = await html2canvas(clone, {
    backgroundColor: "#ffffff",
    scale: window.devicePixelRatio * 2,
    useCORS: true,
    scrollX: 0,
    scrollY: 0,
    width: clone.scrollWidth,
    height: clone.scrollHeight,
  });

  // 7. Download
  const link = document.createElement("a");
  link.download = filename.replace(/[^a-zA-Z0-9]/g, "_") + ".png";
  link.href = canvas.toDataURL("image/png");
  link.click();

  // 8. Cleanup
  document.body.removeChild(clone);
}
